import React, { useState, useEffect } from "react";
import {
  Table,
  Tabs,
  Checkbox,
  message,
  Button,
  Pagination,
  Tag,
  Select,
  Modal,
  TimePicker,
  Space,
  Dropdown,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { notification } from "antd";
import dayjs from "dayjs";
import AdminSidebar from "../components/AdminSidebar";
import { getRequest, postRequest } from "../utils/apicalls";
import "../styles/admindashboard.css";
import mytLogo from "../image/myt logo.d51e67ca4d4eeea6450b.png";

const { TabPane } = Tabs;
const COMPLETED_TASK_CELL_WIDTH = 260;

const CompletedTaskCell = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const taskText = text || "-";
  const hasMore = taskText !== "-";

  return (
    <div
      style={{
        width: `${COMPLETED_TASK_CELL_WIDTH}px`,
        maxWidth: `${COMPLETED_TASK_CELL_WIDTH}px`,
        minWidth: 0,
        textAlign: "left",
      }}
    >
      <div
        style={
          expanded
            ? {
                width: "100%",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                textAlign: "left",
              }
            : {
                width: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }
        }
      >
        {taskText}
      </div>
      {hasMore && (
        <Button
          type="link"
          size="small"
          onClick={() => setExpanded((isExpanded) => !isExpanded)}
          style={{ padding: 0, height: "auto" }}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [data, setData] = useState({
    pending: [],
    approved: [],
    disapproved: [],
    all: [],
  });
  const [loading, setLoading] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState({});

  const [pagination, setPagination] = useState({
    offset: Number(localStorage.getItem("monitoring_offset")) || 0,
    pageSize: 10,
    total: 0,
  });

  const [hasNextPage, setHasNextPage] = useState(false);
  const [activeTab, setActiveTab] = useState(
    localStorage.getItem("monitoring_tab") || "pending"
  );
  const [nameFilter, setNameFilter] = useState("all");
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [disapproveModalOpen, setDisapproveModalOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    time_in_am: null,
    time_out_am: null,
    time_in_pm: null,
    time_out_pm: null,
    total_hours: 0,
  });

  useEffect(() => {
    localStorage.setItem("monitoring_offset", pagination.offset);
  }, [pagination.offset]);

  useEffect(() => {
    fetchAttendance(activeTab, pagination.offset, pagination.pageSize);
  }, [activeTab, pagination.offset, pagination.pageSize]);

  const fetchAttendance = async (status = "pending", offset = 0, limit) => {
    setLoading(true);

    try {
      const url = `timesheets/get_timesheets_by_status?status=${status}&offset=${offset}&limit=${limit}`;
      const response = await getRequest(url);

      if (response?.status === "success") {
        setData((prevData) => ({
          ...prevData,
          [status]: response.data || [],
        }));

        setHasNextPage(response.has_next_page || false);

        setPagination((prev) => ({
          ...prev,
          total: response.size,
        }));
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSelectChange = (record, checked) => {
    setSelectedRecords((prev) => ({ ...prev, [record.id]: checked }));
  };

  const filterByName = (list) => {
    if (nameFilter === "all") return list;
    return list.filter((item) => item.full_name === nameFilter);
  };

  const approveSelected = async () => {
    const approvedIds = Object.keys(selectedRecords).filter(
      (id) => selectedRecords[id]
    );

    if (approvedIds.length === 0) {
      message.warning("No records selected for approval.");
      return;
    }

    // TANANG SELECTED RECORDS
    const selectedList = data.pending.filter((item) =>
      approvedIds.includes(String(item.id))
    );

    // TAN-AWA KUNG NAAY LAPAS 8 HOURS
    const overtimeInterns = selectedList.filter(
      (item) => Number(item.total_hours) > 8
    );

    if (overtimeInterns.length > 0) {
      Modal.warning({
        title: "Approval Not Allowed",
        width: 600,
        content: (
          <>
            <p>
              The following intern(s) rendered more than <b>8 hours</b>:
            </p>

            <ul style={{ paddingLeft: 20 }}>
              {overtimeInterns.map((item) => (
                <li key={item.id}>
                  {item.full_name} ({item.total_hours} hrs)
                </li>
              ))}
            </ul>

            <p style={{ marginTop: 15 }}>
              Please use <b>Action → APPROVE OVERTIME</b> for these intern(s).
            </p>
          </>
        ),
      });

      return;
    }

    Modal.confirm({
      title: "Approve Timesheets",
      content: "Are you sure you want to approve the selected intern(s)?",

      okText: "Yes",
      cancelText: "Cancel",

      onOk: async () => {
        const requester = localStorage.getItem("requester");

        try {
          await postRequest("timesheets/approve_timesheets", {
            requester,
            timesheet_ids: approvedIds,
          });

          message.success("Selected records approved successfully.");

          fetchAttendance("pending", pagination.offset, pagination.pageSize);

          setSelectedRecords({});
        } catch (error) {
          message.error("Failed to approve records.");
        }
      },
    });
  };

  const approveOvertime = async () => {
    if (!selectedRecord) return;

    const requester = localStorage.getItem("requester");

    try {
      await postRequest("timesheets/approve_timesheets", {
        requester,
        timesheet_ids: [selectedRecord.id],
      });

      Modal.success({
        title: "Overtime Approved",
        content: (
          <div>
            <p>The overtime attendance has been approved successfully.</p>
            <p>
              <strong>Intern:</strong> {selectedRecord?.full_name}
            </p>
            <p>
              <strong>Date:</strong> {selectedRecord?.date}
            </p>
            <p>
              <strong>Total Hours:</strong> {selectedRecord?.total_hours} hrs
            </p>
          </div>
        ),
        okText: "OK",
        centered: true,
        width: 550,
      });

      setApproveModalOpen(false);
      setSelectedRecord(null);

      fetchAttendance("pending", pagination.offset, pagination.pageSize);
    } catch (error) {
      message.error("Failed to approve overtime.");
    }
  };

  const disapproveSelected = async () => {
    const disapprovedIds = Object.keys(selectedRecords).filter(
      (id) => selectedRecords[id]
    );

    if (disapprovedIds.length === 0) {
      message.warning("No records selected for disapproval.");
      return;
    }

    Modal.confirm({
      title: "Disapprove Timesheets",
      content: "Are you sure you want to disapprove the selected intern(s)?",

      okText: "Yes",
      cancelText: "Cancel",

      onOk: async () => {
        const requester = localStorage.getItem("requester");

        try {
          await postRequest("timesheets/disapprove_timesheets", {
            requester,
            timesheet_ids: disapprovedIds,
          });

          message.success("Selected records disapproved successfully.");

          fetchAttendance("pending", pagination.offset, pagination.pageSize);

          setSelectedRecords({});
        } catch (error) {
          message.error("Failed to disapprove records.");
        }
      },
    });
  };

  const convertTo12HourFormat = (time) => {
    if (!time) return "";
    const [hour, minute] = time.split(":");
    const date = new Date();
    date.setHours(hour, minute);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const parseTimeToDayjs = (time) => {
    if (!time) return null;
    return dayjs(time, "HH:mm:ss").second(0).millisecond(0);
  };

  const formatTimeForApi = (value) => {
    if (!value) return null;
    return dayjs(value).second(0).millisecond(0).format("HH:mm:ss");
  };

  const getDurationHours = (start, end) => {
    if (!start || !end) return 0;
    const startTime = dayjs(start).second(0).millisecond(0);
    const endTime = dayjs(end).second(0).millisecond(0);

    if (
      !startTime.isValid() ||
      !endTime.isValid() ||
      endTime.isBefore(startTime)
    ) {
      return 0;
    }

    return endTime.diff(startTime, "minute") / 60;
  };

  const calculateTotalHours = (form) => {
    const morningHours = getDurationHours(form.time_in_am, form.time_out_am);
    const afternoonHours = getDurationHours(form.time_in_pm, form.time_out_pm);
    return Number((morningHours + afternoonHours).toFixed(2));
  };

  const openAdjustModal = (record) => {
    const initialForm = {
      time_in_am: parseTimeToDayjs(record.time_in_am),
      time_out_am: parseTimeToDayjs(record.time_out_am),
      time_in_pm: parseTimeToDayjs(record.time_in_pm),
      time_out_pm: parseTimeToDayjs(record.time_out_pm),
      total_hours: 0,
    };

    initialForm.total_hours = calculateTotalHours(initialForm);

    setSelectedTimesheet(record);
    setAdjustForm(initialForm);
    setAdjustModalOpen(true);
  };

  const openApproveModal = (record) => {
    setSelectedRecord(record);
    setApproveModalOpen(true);
  };

  const openDisapproveModal = (record) => {
    setSelectedRecord(record);
    setDisapproveModalOpen(true);
  };
  const handleAdjustTimeChange = (field, value) => {
    setAdjustForm((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      return {
        ...updated,
        total_hours: calculateTotalHours(updated),
      };
    });
  };

  const handleSaveAdjustedTime = async () => {
    if (!selectedTimesheet) return;

    const requester = localStorage.getItem("requester");
    const token = localStorage.getItem("token");

    if (
      adjustForm.time_in_am &&
      adjustForm.time_out_am &&
      dayjs(adjustForm.time_out_am).isBefore(dayjs(adjustForm.time_in_am))
    ) {
      message.error(
        "Morning end time cannot be earlier than morning start time."
      );
      return;
    }

    if (
      adjustForm.time_in_pm &&
      adjustForm.time_out_pm &&
      dayjs(adjustForm.time_out_pm).isBefore(dayjs(adjustForm.time_in_pm))
    ) {
      message.error(
        "Afternoon end time cannot be earlier than afternoon start time."
      );
      return;
    }

    setAdjustLoading(true);

    try {
      const payload = {
        requester,
        token,
        timesheet_id: selectedTimesheet.id,
        time_in_am: formatTimeForApi(adjustForm.time_in_am),
        time_out_am: formatTimeForApi(adjustForm.time_out_am),
        time_in_pm: formatTimeForApi(adjustForm.time_in_pm),
        time_out_pm: formatTimeForApi(adjustForm.time_out_pm),
      };

      const response = await postRequest(
        "timesheets/update_timesheet_time",
        payload
      );

      if (response?.status === "success") {
        message.success("Timesheet updated successfully.");
        setAdjustModalOpen(false);
        setSelectedTimesheet(null);
        fetchAttendance("pending", pagination.offset, pagination.pageSize);
      } else {
        message.error(response?.message || "Failed to update timesheet.");
      }
    } catch (error) {
      console.error("Error updating timesheet:", error);
      message.error("Failed to update timesheet.");
    } finally {
      setAdjustLoading(false);
    }
  };

  const columnsBase = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) =>
        new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      width: 180,
      ellipsis: true,
    },
    {
      title: "Setup",
      key: "setup",
      render: (record) => {
        const formatModality = (modality) => {
          if (!modality || modality === "Absent") return "";
          return modality === "FTF" ? "F2F" : modality;
        };

        const am = formatModality(record.am_modality || "").trim();
        const pm = formatModality(record.pm_modality || "").trim();

        if (!am && !pm) return "";
        if (am && pm && am.toLowerCase() === pm.toLowerCase()) return am;
        if (!am) return pm;
        if (!pm) return am;

        return `${am} / ${pm}`;
      },
      width: 100,
    },
    {
      title: "Morning",
      children: [
        {
          title: "Start",
          dataIndex: "time_in_am",
          key: "time_in_am",
          render: convertTo12HourFormat,
          width: 100,
        },
        {
          title: "End",
          dataIndex: "time_out_am",
          key: "time_out_am",
          render: convertTo12HourFormat,
          width: 100,
        },
      ],
    },
    {
      title: "Afternoon",
      children: [
        {
          title: "Start",
          dataIndex: "time_in_pm",
          key: "time_in_pm",
          render: convertTo12HourFormat,
          width: 100,
        },
        {
          title: "End",
          dataIndex: "time_out_pm",
          key: "time_out_pm",
          render: convertTo12HourFormat,
          width: 100,
        },
      ],
    },
    {
      title: "# of hours",
      dataIndex: "total_hours",
      key: "total_hours",
      width: 100,
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "AM Completed Task",
      dataIndex: "am_task_completed",
      key: "am_task_completed",
      width: COMPLETED_TASK_CELL_WIDTH,
      render: (text) => <CompletedTaskCell text={text} />,
    },
    {
      title: "PM Completed Task",
      dataIndex: "pm_task_completed",
      key: "pm_task_completed",
      width: COMPLETED_TASK_CELL_WIDTH,
      render: (text) => <CompletedTaskCell text={text} />,
    },
  ];

  const uniqueNames = [
    ...new Set(
      [
        ...data.pending.map((item) => item.full_name),
        ...data.approved.map((item) => item.full_name),
        ...data.disapproved.map((item) => item.full_name),
        ...data.all.map((item) => item.full_name),
      ].filter(Boolean)
    ),
  ];

  const renderPagination = () => (
    <div className="mt-4 flex justify-start lg:justify-end">
      <Pagination
        current={pagination.offset / pagination.pageSize + 1}
        pageSize={10}
        total={pagination.total}
        showSizeChanger={false}
        onChange={(page) => {
          const newOffset = (page - 1) * 10;
          localStorage.setItem("monitoring_offset", newOffset);
          setPagination((prev) => ({
            ...prev,
            offset: newOffset,
          }));
        }}
      />
    </div>
  );

  return (
    <div
      className={`admin-dashboard-container ${
        collapsed ? "collapsed" : "expanded"
      }`}
    >
      <AdminSidebar collapsed={collapsed} onCollapse={setCollapsed} />

      <div className="admin-dashboard-content overflow-y-auto p-4 h-[100vh]">
        <div
          className={`admin-header-container ${
            collapsed ? "" : "expanded-sidebar"
          }`}
        >
          <img src={mytLogo} alt="MYT Logo" className="admin-myt-logo" />
          <div className="admin-header-text">
            <h2 className="admin-company-name text-xl font-bold">
              MYT SoftDev Solutions, Inc.
            </h2>
            <p className="admin-company-address text-sm">
              301 The Greenery, Pope John Paul II Ave, Cebu City, 6000 Cebu
            </p>
          </div>
        </div>

        <h3 className="admin-title-header mt-4 text-lg font-semibold">
          Monitoring Records
        </h3>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "-42px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Select
            showSearch
            allowClear
            value={nameFilter === "all" ? undefined : nameFilter}
            onChange={(value) => setNameFilter(value || "all")}
            style={{ width: 250 }}
            placeholder="Filter Interns"
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {uniqueNames.map((name) => (
              <Select.Option key={name} value={name}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </div>

        <Tabs
          type="card"
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            localStorage.setItem("monitoring_tab", key);
            setPagination((prev) => ({
              ...prev,
              offset: 0,
            }));
            localStorage.setItem("monitoring_offset", 0);
          }}
          className="mt-4"
        >
          {/* ========================= PENDING ========================= */}
          <TabPane tab="Pending" key="pending">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
              <Checkbox
                onChange={(e) => {
                  const checked = e.target.checked;
                  const updatedSelection = {};
                  data.pending.forEach((record) => {
                    updatedSelection[record.id] = checked;
                  });
                  setSelectedRecords(updatedSelection);
                }}
                checked={
                  data.pending.length > 0 &&
                  data.pending.every((record) => selectedRecords[record.id])
                }
                indeterminate={
                  Object.values(selectedRecords).some(Boolean) &&
                  !data.pending.every((record) => selectedRecords[record.id])
                }
              >
                Select All
              </Checkbox>

              <div className="hidden md:flex gap-2">
                <Button type="primary" onClick={approveSelected}>
                  Approve
                </Button>
                <Button type="primary" danger onClick={disapproveSelected}>
                  Disapprove
                </Button>
              </div>
            </div>

            <div className="admin-monitoring-table-wrapper">
              <Table
                columns={[
                  {
                    title: "",
                    key: "select",
                    render: (_, record) => (
                      <Checkbox
                        checked={selectedRecords[record.id] || false}
                        onChange={(e) =>
                          onSelectChange(record, e.target.checked)
                        }
                      />
                    ),
                    width: 50,
                  },
                  {
                    title: "Name",
                    dataIndex: "full_name",
                    key: "full_name",
                    width: 220,
                    ellipsis: true,
                  },
                  ...columnsBase,
                  {
                    title: "Action",
                    key: "action",
                    width: 150,
                    render: (_, record) => {
                      const items = [
                        {
                          key: "approve",
                          label: "APPROVE",
                          onClick: () => openApproveModal(record),
                        },
                        {
                          key: "disapprove",
                          label: "DISAPPROVE",
                          onClick: () => openDisapproveModal(record),
                        },
                      ];

                      return (
                        <Dropdown menu={{ items }} trigger={["click"]}>
                          <Button>
                            Action <DownOutlined />
                          </Button>
                        </Dropdown>
                      );
                    },
                  },
                ]}
                dataSource={filterByName(data.pending)}
                rowKey="id"
                loading={loading}
                pagination={false}
                className="admin-monitoring-table"
                scroll={{ x: "max-content" }}
              />
            </div>

            <div className="flex flex-wrap md:hidden gap-2 mt-4">
              <Button type="primary" onClick={approveSelected}>
                Approve
              </Button>
              <Button type="primary" danger onClick={disapproveSelected}>
                Disapprove
              </Button>
            </div>

            {renderPagination()}
          </TabPane>

          {/* ========================= APPROVED ========================= */}
          <TabPane tab="Approved" key="approved">
            <div className="admin-monitoring-table-wrapper">
              <Table
                columns={[
                  {
                    title: "Name",
                    dataIndex: "full_name",
                    key: "full_name",
                    width: 220,
                    ellipsis: true,
                  },
                  ...columnsBase,
                  {
                    title: "Approved By",
                    key: "approved_by",
                    dataIndex: "approved_by_name",
                    width: 220,
                    render: (_, record) => {
                      const approver = record.approved_by_name;
                      const approvedOn = record.approved_on
                        ? new Date(record.approved_on).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )
                        : "";
                      return approver ? `${approver} (${approvedOn})` : "";
                    },
                  },
                ]}
                dataSource={filterByName(data.approved)}
                rowKey="id"
                loading={loading}
                pagination={false}
                className="admin-monitoring-table"
                scroll={{ x: "max-content" }}
              />
            </div>
            {renderPagination()}
          </TabPane>

          {/* ========================= DISAPPROVED ========================= */}
          <TabPane tab="Disapproved" key="disapproved">
            <div className="admin-monitoring-table-wrapper">
              <Table
                columns={[
                  {
                    title: "Name",
                    dataIndex: "full_name",
                    key: "full_name",
                    width: 220,
                    ellipsis: true,
                  },
                  ...columnsBase,
                  {
                    title: "Disapproved By",
                    key: "disapproved_by_name",
                    dataIndex: "disapproved_by_name",
                    width: 220,
                    render: (_, record) => {
                      const disapprover = record.disapproved_by_name;
                      const disapprovedOn = record.disapproved_on
                        ? new Date(record.disapproved_on).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )
                        : "";
                      return disapprover
                        ? `${disapprover} (${disapprovedOn})`
                        : "";
                    },
                  },
                ]}
                dataSource={filterByName(data.disapproved)}
                rowKey="id"
                loading={loading}
                pagination={false}
                className="admin-monitoring-table"
                scroll={{ x: "max-content" }}
              />
            </div>
            {renderPagination()}
          </TabPane>

          {/* ========================= ALL ========================= */}
          <TabPane tab="All" key="all">
            <div className="admin-monitoring-table-wrapper">
              <Table
                columns={[
                  {
                    title: "Name",
                    dataIndex: "full_name",
                    key: "full_name",
                    width: 220,
                    ellipsis: true,
                  },
                  ...columnsBase,
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    width: 100,
                    render: (text) => {
                      let color = "default";
                      if (text === "approved") color = "green";
                      else if (text === "disapproved") color = "red";
                      else if (text === "pending") color = "blue";

                      const capitalizedText =
                        text?.charAt(0).toUpperCase() + text?.slice(1);

                      return text === "absent" ? (
                        ""
                      ) : (
                        <Tag color={color}>{capitalizedText}</Tag>
                      );
                    },
                  },
                ]}
                dataSource={filterByName(data.all)}
                rowKey="id"
                loading={loading}
                pagination={false}
                className="admin-monitoring-table"
                scroll={{ x: "max-content" }}
              />
            </div>
            {renderPagination()}
          </TabPane>
        </Tabs>
      </div>

      {/* ADJUST TIME */}
      <Modal
        title="Approve Timesheet"
        open={approveModalOpen}
        footer={null}
        onCancel={() => {
          setApproveModalOpen(false);
          setSelectedRecord(null);
        }}
      >
        <p>Are you sure you want to approve?</p>

        <div className="approve-modal-buttons">
          {Number(selectedRecord?.total_hours) > 8 ? (
            <Button type="primary" onClick={approveOvertime}>
              Approve Overtime
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={async () => {
                const requester = localStorage.getItem("requester");

                try {
                  await postRequest("timesheets/approve_timesheets", {
                    requester,
                    timesheet_ids: [selectedRecord.id],
                  });

                  message.success("Timesheet approved successfully.");

                  setApproveModalOpen(false);
                  setSelectedRecord(null);

                  fetchAttendance(
                    "pending",
                    pagination.offset,
                    pagination.pageSize
                  );
                } catch (error) {
                  message.error("Failed to approve.");
                }
              }}
            >
              Approve Time
            </Button>
          )}

          <Button
            className="adjust-time-btn"
            onClick={() => {
              setApproveModalOpen(false);
              openAdjustModal(selectedRecord);
            }}
          >
            Adjust Time
          </Button>
        </div>
      </Modal>
      <Modal
        title="Disapprove Timesheet"
        open={disapproveModalOpen}
        onCancel={() => {
          setDisapproveModalOpen(false);
          setSelectedRecord(null);
        }}
        onOk={async () => {
          const requester = localStorage.getItem("requester");

          try {
            await postRequest("timesheets/disapprove_timesheets", {
              requester,
              timesheet_ids: [selectedRecord.id],
            });

            message.success("Timesheet disapproved successfully.");

            setDisapproveModalOpen(false);
            setSelectedRecord(null);

            fetchAttendance("pending", pagination.offset, pagination.pageSize);
          } catch (error) {
            message.error("Failed to disapprove.");
          }
        }}
        okText="Yes"
        cancelText="Cancel"
      >
        <p>Are you sure you want to disapprove?</p>
      </Modal>

      <Modal
        title="Adjust Timesheet"
        open={adjustModalOpen}
        onCancel={() => {
          setAdjustModalOpen(false);
          setSelectedTimesheet(null);
        }}
        onOk={handleSaveAdjustedTime}
        confirmLoading={adjustLoading}
        okText="Save Changes"
        cancelText="Cancel"
        width={650}
      >
        <div className="adjust-modal-content">
          {/* Intern Details */}
          <div className="adjust-info-card">
            <div className="adjust-info-item">
              <span className="adjust-info-label">Intern</span>
              <span className="adjust-info-value">
                {selectedTimesheet?.full_name || "-"}
              </span>
            </div>

            <div className="adjust-info-item">
              <span className="adjust-info-label">Date</span>
              <span className="adjust-info-value">
                {selectedTimesheet?.date
                  ? new Date(selectedTimesheet.date).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )
                  : "-"}
              </span>
            </div>
          </div>

          {/* Morning Session */}
          <div className="adjust-session-card">
            <h4 className="adjust-session-title">Morning Session</h4>

            <div className="adjust-time-grid">
              <div>
                <label className="adjust-label">Time In</label>

                <TimePicker
                  className="adjust-time-picker"
                  value={adjustForm.time_in_am}
                  onChange={(value) =>
                    handleAdjustTimeChange("time_in_am", value)
                  }
                  format="hh:mm A"
                  use12Hours
                  minuteStep={5}
                  needConfirm={false}
                />
              </div>

              <div>
                <label className="adjust-label">Time Out</label>

                <TimePicker
                  className="adjust-time-picker"
                  value={adjustForm.time_out_am}
                  onChange={(value) =>
                    handleAdjustTimeChange("time_out_am", value)
                  }
                  format="hh:mm A"
                  use12Hours
                  minuteStep={5}
                  needConfirm={false}
                />
              </div>
            </div>
          </div>

          {/* Afternoon Session */}
          <div className="adjust-session-card">
            <h4 className="adjust-session-title">Afternoon Session</h4>

            <div className="adjust-time-grid">
              <div>
                <label className="adjust-label">Time In</label>

                <TimePicker
                  className="adjust-time-picker"
                  value={adjustForm.time_in_pm}
                  onChange={(value) =>
                    handleAdjustTimeChange("time_in_pm", value)
                  }
                  format="hh:mm A"
                  use12Hours
                  minuteStep={5}
                  needConfirm={false}
                />
              </div>

              <div>
                <label className="adjust-label">Time Out</label>

                <TimePicker
                  className="adjust-time-picker"
                  value={adjustForm.time_out_pm}
                  onChange={(value) =>
                    handleAdjustTimeChange("time_out_pm", value)
                  }
                  format="hh:mm A"
                  use12Hours
                  minuteStep={5}
                  needConfirm={false}
                />
              </div>
            </div>
          </div>

          {/* Total Hours */}
          <div className="adjust-total-wrapper left">
            <span className="adjust-total-text">
              Total Rendered Hours:{" "}
              {Number(adjustForm.total_hours || 0).toFixed(2)} hrs
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
